import { HistoryOutlined } from '@ant-design/icons'
import { Button, Input, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import { ROUTE } from '../../utils/constant'
import './EmailSendHistory.scss'

const { Text } = Typography

const STATUS_COLOR = {
	queued: 'gold',
	running: 'processing',
	completed: 'success',
	failed: 'error',
	cancelled: 'default',
}

const CANCELLABLE = new Set(['queued', 'running'])

const formatTime = value => {
	if (!value) return '—'
	try {
		return new Date(value).toLocaleString()
	} catch {
		return String(value)
	}
}

export const EmailSendHistory = () => {
	const history = useHistory()
	const [loading, setLoading] = useState(false)
	const [items, setItems] = useState([])
	const [total, setTotal] = useState(0)
	const [page, setPage] = useState(0)
	const [pageSize, setPageSize] = useState(20)
	const [status, setStatus] = useState(undefined)
	const [emailType, setEmailType] = useState(undefined)
	const [query, setQuery] = useState('')
	const [cancellingJobID, setCancellingJobID] = useState(null)

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const rs = await api.emailAdminApi.listSendJobs({
				page,
				pageSize,
				status,
				emailType,
				q: query,
			})
			setItems(Array.isArray(rs?.data) ? rs.data : [])
			setTotal(Number(rs?.total) || 0)
		} catch (err) {
			message.error(err?.message || 'Could not load send history.')
		} finally {
			setLoading(false)
		}
	}, [page, pageSize, status, emailType, query])

	useEffect(() => {
		load()
	}, [load])

	const handleCancelJob = useCallback(
		async record => {
			if (!record?.jobID) return
			setCancellingJobID(record.jobID)
			try {
				await api.emailAdminApi.cancelSendJob(record.jobID)
				message.success('Cancel request sent.')
				await load()
			} catch (err) {
				message.error(err?.message || 'Cancel failed.')
			} finally {
				setCancellingJobID(null)
			}
		},
		[load]
	)

	const columns = [
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 170,
			render: formatTime,
		},
		{
			title: 'Job ID',
			dataIndex: 'jobID',
			key: 'jobID',
			width: 160,
			ellipsis: true,
			render: value =>
				value ? (
					<Text
						copyable={{ text: value, tooltips: ['Copy job ID', 'Copied'] }}
						ellipsis={{ tooltip: value }}
						style={{ maxWidth: 140 }}
					>
						{value}
					</Text>
				) : (
					'—'
				),
		},
		{
			title: 'Subject',
			dataIndex: 'subject',
			key: 'subject',
			ellipsis: true,
			render: (value, record) => value || record.jobID || '—',
		},
		{
			title: 'Template',
			dataIndex: 'templateName',
			key: 'templateName',
			width: 160,
			ellipsis: true,
			render: value => value || '—',
		},
		{
			title: 'Type',
			dataIndex: 'emailType',
			key: 'emailType',
			width: 120,
		},
		{
			title: 'Scope',
			dataIndex: 'scope',
			key: 'scope',
			width: 120,
			render: (scope, record) =>
				scope === 'single_user' ? record.targetEmail || '1 user' : 'All users',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 110,
			render: value => (
				<Tag color={STATUS_COLOR[value] || 'default'}>{value}</Tag>
			),
		},
		{
			title: 'Sent / Fail / Skip',
			key: 'counts',
			width: 150,
			render: (_, record) => {
				const c = record.recipientCounts
				const r = record.result
				if (c && c.total > 0) {
					return `${c.sent} / ${c.failed} / ${c.skipped}`
				}
				if (r) {
					return `${r.emailsSent || 0} / ${r.emailsFailed || 0} / ${r.emailsSkipped || 0}`
				}
				return '—'
			},
		},
		{
			title: 'Delivered / Bounce / Open',
			key: 'engagement',
			width: 170,
			render: (_, record) => {
				const c = record.recipientCounts
				if (!c || !c.total) return '—'
				return `${c.delivered || 0} / ${c.bounced || 0} / ${c.opened || 0}`
			},
		},
		{
			title: '',
			key: 'actions',
			width: 160,
			render: (_, record) => (
				<Space size={0}>
					<Button
						type="link"
						onClick={() =>
							history.push(
								`${ROUTE.EMAIL_CONTENT_MANAGEMENT}${ROUTE.EMAIL_SEND_HISTORY}/${record.jobID}`
							)
						}
					>
						Detail
					</Button>
					{CANCELLABLE.has(record.status) ? (
						<Popconfirm
							title="Cancel this send job? Sending will stop after the current recipient."
							okText="Cancel job"
							cancelText="Keep sending"
							okButtonProps={{
								danger: true,
								loading: cancellingJobID === record.jobID,
							}}
							onConfirm={() => handleCancelJob(record)}
						>
							<Button
								type="link"
								danger
								loading={cancellingJobID === record.jobID}
							>
								Cancel
							</Button>
						</Popconfirm>
					) : null}
				</Space>
			),
		},
	]

	return (
		<ListPageShell className="email-send-history">
			<ListPageIntro
				icon={HistoryOutlined}
				title="Send history"
				subtitle="Past email campaigns: audience, template, content, and per-recipient status."
			/>
			<div className="email-send-history__toolbar">
				<Space wrap>
					<Input.Search
						allowClear
						placeholder="Search subject, job id, email"
						onSearch={value => {
							setPage(0)
							setQuery(value || '')
						}}
						style={{ width: 280 }}
					/>
					<Select
						allowClear
						placeholder="Status"
						style={{ width: 140 }}
						value={status}
						onChange={value => {
							setPage(0)
							setStatus(value)
						}}
						options={[
							{ value: 'queued', label: 'Queued' },
							{ value: 'running', label: 'Running' },
							{ value: 'completed', label: 'Completed' },
							{ value: 'failed', label: 'Failed' },
							{ value: 'cancelled', label: 'Cancelled' },
						]}
					/>
					<Select
						allowClear
						placeholder="Type"
						style={{ width: 160 }}
						value={emailType}
						onChange={value => {
							setPage(0)
							setEmailType(value)
						}}
						options={[
							{ value: 'transactional', label: 'Transactional' },
							{ value: 'marketing', label: 'Marketing' },
							{ value: 'newsletter', label: 'Newsletter' },
							{ value: 'other', label: 'Other' },
						]}
					/>
					<Button onClick={load}>Refresh</Button>
				</Space>
			</div>
			<Table
				rowKey="jobID"
				loading={loading}
				columns={columns}
				dataSource={items}
				pagination={{
					current: page + 1,
					pageSize,
					total,
					showSizeChanger: true,
					onChange: (nextPage, nextSize) => {
						setPage(nextPage - 1)
						setPageSize(nextSize)
					},
				}}
			/>
		</ListPageShell>
	)
}
